"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { authClient } from "@/lib/auth/client";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Convert OAuth error codes to user-friendly messages
 */
function getOAuthErrorMessage(error: string): string {
  const errorMessages: Record<string, string> = {
    oauth_error: "Failed to sign in with Google. Please try again.",
    auth_error: "Authentication failed. Please try again.",
    internal_server_error: "An unexpected error occurred. Please try again.",
    access_denied: "Access was denied. Please try again.",
  };
  return errorMessages[error] || "An error occurred during sign in. Please try again.";
}

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") ?? "/dashboard";

  // Check for OAuth error from query params
  const oauthError = searchParams.get("error");
  const errorMessage = oauthError ? getOAuthErrorMessage(oauthError) : null;

  // Debug: Log URL errors on mount
  if (typeof window !== "undefined" && oauthError) {
  }

  const [isLoading, setIsLoading] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [error, setError] = useState<string | null>(errorMessage);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginFormData) {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        throw new Error(result.error.message ?? "Failed to sign in");
      }

      toast.success("Welcome back!");
      router.push(redirect);
      router.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleMagicLink() {
    // Read email out of the form via the DOM since the schema requires both
    // email + password but magic-link only needs email.
    const emailEl = document.getElementById("email") as HTMLInputElement | null;
    const email = emailEl?.value?.trim();
    if (!email) {
      setError("Enter your email first to get a magic link.");
      emailEl?.focus();
      return;
    }
    setIsMagicLinkLoading(true);
    setError(null);
    try {
      const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
      const callbackUrl = `${baseUrl}${redirect}`;
      const result = await authClient.signIn.magicLink({
        email,
        callbackURL: callbackUrl,
      });
      if (result.error) {
        throw new Error(result.error.message ?? "Couldn't send magic link");
      }
      setMagicLinkSent(true);
      toast.success("Check your inbox for the sign-in link.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send magic link");
    } finally {
      setIsMagicLinkLoading(false);
    }
  }

  async function handleGoogleOAuth() {
    setIsOAuthLoading(true);
    setError(null);

    try {
      // Use absolute URL for callback to ensure redirect goes to web app, not API
      const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
      const callbackUrl = `${baseUrl}${redirect}`;

      const _result = await authClient.signIn.social({
        provider: "google",
        callbackURL: callbackUrl,
        errorCallbackURL: `${baseUrl}/login?error=oauth_error`,
      });
    } catch (err) {
      console.error("[OAuth Debug] signIn.social error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsOAuthLoading(false);
    }
  }

  const isAnyLoading = isLoading || isOAuthLoading || isMagicLinkLoading;

  return (
    <Card className="animate-fade-in-up border-border/50 shadow-lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        {error && (
          <CardHeader className="pb-0">
            <div className="flex animate-shake items-start gap-3 rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          </CardHeader>
        )}

        <CardContent className="space-y-5">
          {/* Google OAuth */}
          <Button
            variant="outline"
            type="button"
            disabled={isAnyLoading}
            onClick={handleGoogleOAuth}
            className="stagger-1 h-11 w-full animate-fade-in-up gap-2 font-medium"
            style={{ animationFillMode: "forwards" }}
          >
            {isOAuthLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <GoogleIcon className="h-5 w-5" />
            )}
            Continue with Google
          </Button>

          <div
            className="stagger-2 relative w-full animate-fade-in-up py-2"
            style={{ animationFillMode: "forwards" }}
          >
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground">or continue with email</span>
            </div>
          </div>

          <div
            className="stagger-3 animate-fade-in-up space-y-2"
            style={{ animationFillMode: "forwards" }}
          >
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              disabled={isAnyLoading}
              error={!!errors.email}
              className={cn("h-11", errors.email && "animate-shake")}
              {...register("email")}
            />
            {errors.email && (
              <p className="flex animate-fade-in-down items-center gap-1 text-destructive text-sm">
                <AlertCircle className="h-3 w-3" />
                {errors.email.message}
              </p>
            )}
          </div>

          <div
            className="stagger-4 animate-fade-in-up space-y-2"
            style={{ animationFillMode: "forwards" }}
          >
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/forgot-password"
                className="text-muted-foreground text-xs transition-colors hover:text-primary"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={isAnyLoading}
              error={!!errors.password}
              className={cn("h-11", errors.password && "animate-shake")}
              {...register("password")}
            />
            {errors.password && (
              <p className="flex animate-fade-in-down items-center gap-1 text-destructive text-sm">
                <AlertCircle className="h-3 w-3" />
                {errors.password.message}
              </p>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-3 pt-2">
          <Button
            type="submit"
            className="stagger-5 h-11 w-full animate-fade-in-up"
            style={{ animationFillMode: "forwards" }}
            disabled={isAnyLoading}
            loading={isLoading}
          >
            Sign in
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-10 w-full text-sm"
            onClick={handleMagicLink}
            disabled={isAnyLoading || magicLinkSent}
          >
            {isMagicLinkLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {magicLinkSent
              ? "Magic link sent — check your email"
              : "Email me a magic link instead"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
